import { useState, useRef } from 'react';

export default function PhotoCapture({ photos = [], onChange }) {
  const fileInputRef = useRef(null);
  const [caption, setCaption] = useState('');
  const [category, setCategory] = useState('general');

  const categories = [
    { id: 'general', label: 'General' },
    { id: 'foh', label: 'Front of House' },
    { id: 'boh', label: 'Back of House' },
    { id: 'bar', label: 'Bar' },
    { id: 'menu', label: 'Menu / Food' },
    { id: 'ambiance', label: 'Ambiance / Design' },
    { id: 'cleanliness', label: 'Cleanliness' },
    { id: 'signage', label: 'Signage / Branding' },
    { id: 'issue', label: 'Issue / Finding' },
  ];

  const handleCapture = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const newPhoto = {
        id: `photo_${Date.now()}`,
        dataUrl: ev.target.result,
        fileName: file.name,
        category,
        caption: caption || '',
        timestamp: new Date().toISOString(),
      };
      onChange([...photos, newPhoto]);
      setCaption('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = (photoId) => {
    onChange(photos.filter(p => p.id !== photoId));
  };

  const updateCaption = (photoId, newCaption) => {
    onChange(photos.map(p => p.id === photoId ? { ...p, caption: newCaption } : p));
  };

  const grouped = {};
  categories.forEach(c => {
    grouped[c.id] = photos.filter(p => p.category === c.id);
  });

  return (
    <div className="card p-6">
      <h3 className="font-display text-lg text-white mb-1">Photos</h3>
      <p className="text-xs text-neutral-500 mb-5">Capture on-site findings</p>

      {/* Capture controls */}
      <div className="bg-neutral-800/40 rounded-lg p-4 mb-5">
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="input-field text-sm py-2 sm:w-40"
          >
            {categories.map(c => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Caption (optional)"
            className="input-field text-sm py-2 flex-1"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="btn-primary text-sm py-2 whitespace-nowrap"
          >
            Take Photo / Upload
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleCapture}
          className="hidden"
        />
      </div>

      {/* Photo grid by category */}
      {photos.length === 0 ? (
        <p className="text-neutral-600 text-sm text-center py-4">No photos yet. Use the button above or tap on mobile to capture.</p>
      ) : (
        <div className="space-y-4">
          {categories.map(cat => {
            const catPhotos = grouped[cat.id] || [];
            if (catPhotos.length === 0) return null;
            return (
              <div key={cat.id}>
                <p className="text-xs text-brand-gold font-semibold mb-2 uppercase tracking-wider">{cat.label} ({catPhotos.length})</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {catPhotos.map(photo => (
                    <div key={photo.id} className="relative group">
                      <img
                        src={photo.dataUrl}
                        alt={photo.caption || 'Audit photo'}
                        className="w-full h-32 object-cover rounded-lg border border-neutral-700/50"
                      />
                      <button
                        onClick={() => removePhoto(photo.id)}
                        className="absolute top-1 right-1 bg-black/70 text-white text-xs w-5 h-5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        x
                      </button>
                      <input
                        type="text"
                        value={photo.caption}
                        onChange={(e) => updateCaption(photo.id, e.target.value)}
                        placeholder="Add caption..."
                        className="w-full mt-1 bg-transparent border-b border-neutral-700/50 text-xs text-neutral-400 py-1 focus:outline-none focus:border-brand-gold/50"
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[10px] text-neutral-600 mt-4">{photos.length} photo{photos.length !== 1 ? 's' : ''} attached to this audit</p>
    </div>
  );
}
